import type { CashFlowProjectionItem } from './cashFlowProjection';
import type { ForecastMoneyOutBucketKey } from './forecastDetailGroups';
import { forecastQuoteCoverageHubHref } from './financesQuoteCoverageWarnings';

type ForecastTargetItem = Pick<
  CashFlowProjectionItem,
  | 'kind'
  | 'plannedKind'
  | 'projectLabel'
  | 'label'
  | 'amount'
  | 'dueDate'
  | 'anchor'
  | 'projectId'
  | 'dealId'
  | 'taskId'
  | 'billId'
  | 'requisitionId'
  | 'billingItemId'
  | 'contractorInvoiceId'
  | 'submissionId'
  | 'quotePlaceholder'
  | 'previewStoragePath'
  | 'previewFileName'
  | 'previewFileUrl'
>;

/** Where a forecast row goes when clicked. Null when the row is only a subtotal. */
export function forecastDetailHref(item: ForecastTargetItem): string | null {
  if (item.requisitionId) {
    return `/purchasing?pr=${encodeURIComponent(item.requisitionId)}`;
  }
  if (item.billId) {
    return `/billing-payables?bill=${encodeURIComponent(item.billId)}`;
  }
  if (item.billingItemId) {
    return `/billing-payables?item=${encodeURIComponent(item.billingItemId)}`;
  }
  if (item.kind === 'ar' && item.dealId) {
    return `/billing-payables?deal=${encodeURIComponent(item.dealId)}`;
  }
  if (item.contractorInvoiceId) return '/finances/contractor-payables';
  if ((item.quotePlaceholder || item.submissionId) && item.projectId) {
    return forecastQuoteCoverageHubHref(item.projectId);
  }
  if (item.projectId && item.taskId) {
    return `/project-hub/${encodeURIComponent(item.projectId)}?task=${encodeURIComponent(item.taskId)}`;
  }
  if (item.projectId) return `/project-hub/${encodeURIComponent(item.projectId)}`;
  return null;
}

/** Project groups open the project. A vendor group opens its PO, or Purchasing when it holds several. */
export function forecastGroupHref(group: {
  key?: string;
  projectId?: string;
  items: ForecastTargetItem[];
}): string | null {
  if (group.key && group.key.startsWith('opex:')) return null;
  if (group.projectId) return `/project-hub/${encodeURIComponent(group.projectId)}`;
  const hrefs: string[] = [];
  for (const item of group.items) {
    const href = forecastDetailHref(item);
    if (href && !hrefs.includes(href)) hrefs.push(href);
  }
  if (hrefs.length === 1) return hrefs[0];
  if (group.items.length > 0 && group.items.every((item) => item.requisitionId)) return '/purchasing';
  return null;
}

export type ForecastBucketAction = 'purchasing' | 'burn' | 'cockpit';

export function forecastBucketAction(key: ForecastMoneyOutBucketKey): ForecastBucketAction | null {
  if (key === 'pos') return 'purchasing';
  if (key === 'burn') return 'burn';
  if (key === 'tax' || key === 'bonus') return 'cockpit';
  return null;
}

export type ForecastPeek =
  | { kind: 'bill'; billId: string }
  | { kind: 'file'; name: string; url?: string; storagePath?: string }
  | { kind: 'summary'; title: string; lines: string[] };

const peekMoney = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

/** Space-bar preview for the row under the pointer. A stored file wins; a PO or bill without one still opens a summary. */
export function forecastItemPeek(
  item: ForecastTargetItem,
  opts: { billHasFile: boolean },
): ForecastPeek | null {
  if (item.billId && opts.billHasFile) return { kind: 'bill', billId: item.billId };
  if (item.previewFileUrl || item.previewStoragePath) {
    return {
      kind: 'file',
      name: item.previewFileName || item.label || item.projectLabel,
      url: item.previewFileUrl,
      storagePath: item.previewStoragePath,
    };
  }
  if (!item.requisitionId && !item.billId) return null;
  const due = item.dueDate
    ? `Due ${item.dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : '';
  const lines = [
    item.label && item.label !== item.projectLabel ? item.label : '',
    item.anchor || '',
    due,
    peekMoney(item.amount),
  ].filter(Boolean);
  return {
    kind: 'summary',
    title: item.projectLabel || item.label || 'Details',
    lines,
  };
}
